import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCoachProfileByCoachIdApi,
  getCoachProfileByEmailIdApi,
  updateCoachProfileApi,
} from "../../api/profileSettingsApi";
import * as Endpoints from "../../entities/Endpoints";

// Async thunk to fetch the coach profile
export const fetchCoachProfileByEmailId = createAsyncThunk(
  Endpoints.GET_COACH_PROFILE_BY_EMAIL_ID,
  async ({ emailId, toggleUnit }, { rejectWithValue }) => {
    try {
      const response = await getCoachProfileByEmailIdApi(emailId);

      // Trigger toggleUnit if measurementSystem is available
      if (response?.measurementSystem) {
        toggleUnit(response.measurementSystem || "metric");
      }

      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to load profile");
    }
  }
);

// Async thunk to fetch the coach profile
export const fetchCoachProfile = createAsyncThunk(
  Endpoints.GET_COACH_PROFILE_BY_COACH_ID,
  async ({ coachId }, { rejectWithValue }) => {
    try {
      const response = await getCoachProfileByCoachIdApi(coachId);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to load profile");
    }
  }
);

// Async thunk to update the coach profile
export const updateCoachProfile = createAsyncThunk(
  Endpoints.UPDATE_COACH_PROFILE,
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await updateCoachProfileApi(profileData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update profile"
      );
    }
  }
);

// Profile slice
const profileSlice = createSlice({
  name: "profile",
  initialState: {
    profileData: null,
    isLoading: false,
    error: null,
    updateSuccess: false,
    updateError: false,
  },
  reducers: {
    resetProfileState: (state) => {
      state.updateSuccess = false;
      state.updateError = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetching coach profile (GET)
      .addCase(fetchCoachProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCoachProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileData = action?.payload;
      })
      .addCase(fetchCoachProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.payload;
      })

      // Fetching coach profile (GET)
      .addCase(fetchCoachProfileByEmailId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCoachProfileByEmailId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileData = action?.payload;
      })
      .addCase(fetchCoachProfileByEmailId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.payload;
      })

      // Updating coach profile (POST)
      .addCase(updateCoachProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.updateSuccess = false;
        state.updateError = false;
      })
      .addCase(updateCoachProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileData = action?.payload;
        state.updateSuccess = true;
        state.updateError = false;
      })
      .addCase(updateCoachProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.payload;
        state.updateSuccess = false;
        state.updateError = true;
      });
  },
});

export const { resetProfileState } = profileSlice.actions;

export const profileReducer = profileSlice.reducer;
